import { z } from 'zod'
import type {
  CreateUserContactInfo,
  CreateUserPersonalInfo,
  UpdateCompensationPayload,
  UpdateEmploymentPayload,
  UpdateUserPayload,
  User,
  UserFieldAccess,
} from '@/types/user.types'
import { MaritalStatus, UserGender } from '@/types/user.types'

const employmentTypeSchema = z
  .union([
    z.literal('full-time'),
    z.literal('part-time'),
    z.literal('contract'),
    z.literal('intern'),
    z.literal(''),
  ])
  .optional()

const genderSchema = z
  .union([
    z.literal(UserGender.MALE),
    z.literal(UserGender.FEMALE),
    z.literal(UserGender.OTHER),
    z.literal(''),
  ])
  .optional()

const maritalStatusSchema = z
  .union([
    z.literal(MaritalStatus.SINGLE),
    z.literal(MaritalStatus.MARRIED),
    z.literal(MaritalStatus.DIVORCED),
    z.literal(MaritalStatus.WIDOWED),
    z.literal(MaritalStatus.SEPARATED),
    z.literal(MaritalStatus.DOMESTIC_PARTNERSHIP),
    z.literal(MaritalStatus.OTHER),
    z.literal(''),
  ])
  .optional()

const isoAlpha2OrEmpty = z
  .union([
    z.string().length(2).regex(/^[A-Z]{2}$/, 'Use ISO alpha-2 (e.g. PK)'),
    z.literal(''),
  ])
  .optional()

const emailOrEmpty = z.union([z.string().email('Invalid email'), z.literal('')]).optional()

function dateOrEmpty(message = 'Use a valid date') {
  return z
    .union([
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, message)
        .refine((value) => !Number.isNaN(Date.parse(value)), message),
      z.literal(''),
    ])
    .optional()
}

const dateOfBirthOrEmpty = dateOrEmpty().refine(
  (value) => !value || value <= new Date().toISOString().slice(0, 10),
  'Date of birth cannot be in the future'
)

export const editUserSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  avatar: z.string().optional(),
  personalInfo: z.object({
    dateOfBirth: dateOfBirthOrEmpty,
    gender: genderSchema,
    nationality: isoAlpha2OrEmpty,
    cnicNumber: z.string().optional(),
    passportNumber: z.string().optional(),
    maritalStatus: maritalStatusSchema,
    emergencyContact: z.object({
      name: z.string().optional(),
      relationship: z.string().optional(),
      phone: z.string().optional(),
      email: emailOrEmpty,
    }),
  }),
  contactInfo: z.object({
    personalEmail: emailOrEmpty,
    phone: z.string().optional(),
    alternatePhone: z.string().optional(),
    address: z.object({
      street: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
      postalCode: z.string().optional(),
    }),
  }),
  employment: z.object({
    department: z.string().optional(),
    designation: z.string().optional(),
    employmentType: employmentTypeSchema,
    joiningDate: dateOrEmpty(),
    workLocation: z
      .union([z.literal('remote'), z.literal('onsite'), z.literal('hybrid'), z.literal('')])
      .optional(),
  }),
  compensation: z.object({
    compensationType: z
      .union([z.literal('salaried'), z.literal('hourly'), z.literal('')])
      .optional(),
    // Numeric values are kept as strings in the form (text/number inputs) and
    // coerced on submit; blank means "leave unchanged".
    baseSalary: z.string().optional(),
    hourlyRate: z.string().optional(),
    currency: z.string().optional(),
    bankDetails: z.object({
      bankName: z.string().optional(),
      accountNumber: z.string().optional(),
    }),
    taxInfo: z.object({
      taxNumber: z.string().optional(),
      taxBracket: z.string().optional(),
      taxExempt: z.boolean().optional(),
    }),
  }),
})

export type EditUserFormValues = z.infer<typeof editUserSchema>

function trim(v: string | undefined): string | undefined {
  const t = v?.trim()
  return t ? t : undefined
}

const FULL_ACCESS: UserFieldAccess = { sensitive: true, payRates: true }

function compactPersonalInfo(
  pi: EditUserFormValues['personalInfo']
): CreateUserPersonalInfo | undefined {
  const out: CreateUserPersonalInfo = {}
  if (trim(pi.dateOfBirth)) out.dateOfBirth = trim(pi.dateOfBirth)
  if (pi.gender) out.gender = pi.gender as CreateUserPersonalInfo['gender']
  if (trim(pi.nationality)) out.nationality = trim(pi.nationality)
  if (trim(pi.cnicNumber)) out.cnicNumber = trim(pi.cnicNumber)
  if (trim(pi.passportNumber)) out.passportNumber = trim(pi.passportNumber)
  if (pi.maritalStatus) out.maritalStatus = pi.maritalStatus as CreateUserPersonalInfo['maritalStatus']
  const ec = pi.emergencyContact
  const emergency = {
    name: trim(ec.name),
    relationship: trim(ec.relationship),
    phone: trim(ec.phone),
    email: trim(ec.email),
  }
  if (Object.values(emergency).some((v) => v !== undefined)) {
    out.emergencyContact = Object.fromEntries(
      Object.entries(emergency).filter(([, v]) => v !== undefined)
    )
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function compactContactInfo(
  ci: EditUserFormValues['contactInfo']
): CreateUserContactInfo | undefined {
  const out: CreateUserContactInfo = {}
  if (trim(ci.personalEmail)) out.personalEmail = trim(ci.personalEmail)
  if (trim(ci.phone)) out.phone = trim(ci.phone)
  if (trim(ci.alternatePhone)) out.alternatePhone = trim(ci.alternatePhone)
  const addr = ci.address
  const address = {
    street: trim(addr.street),
    city: trim(addr.city),
    state: trim(addr.state),
    country: trim(addr.country),
    postalCode: trim(addr.postalCode),
  }
  const compactAddr = Object.fromEntries(
    Object.entries(address).filter(([, v]) => v !== undefined)
  )
  if (Object.keys(compactAddr).length > 0) out.address = compactAddr
  return Object.keys(out).length > 0 ? out : undefined
}

function toNumberOrUndefined(v: string | undefined): number | undefined {
  const t = v?.trim()
  if (!t) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

function normalizeDate(value: string | undefined): string | undefined {
  const t = trim(value)
  return t ? t.slice(0, 10) : undefined
}

/** Compensation is saved via a separate endpoint (PUT /users/:id/compensation).
 *  Returns undefined when nothing was entered so we can skip the extra request.
 *  Fields the API hid from this viewer are never sent (the API refuses them). */
export function formValuesToCompensationPayload(
  values: EditUserFormValues,
  access: UserFieldAccess = FULL_ACCESS
): UpdateCompensationPayload | undefined {
  const out = compensationFields(values.compensation)
  if (!access.payRates) {
    delete out.compensationType
    delete out.baseSalary
    delete out.hourlyRate
    delete out.currency
  }
  if (!access.sensitive) {
    delete out.bankDetails
    delete out.taxInfo
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function compensationFields(c: EditUserFormValues['compensation']): UpdateCompensationPayload {
  const out: UpdateCompensationPayload = {}
  if (c.compensationType) out.compensationType = c.compensationType
  if (c.compensationType === 'hourly') {
    const hourlyRate = toNumberOrUndefined(c.hourlyRate)
    if (hourlyRate !== undefined) out.hourlyRate = hourlyRate
  } else if (c.compensationType === 'salaried') {
    const baseSalary = toNumberOrUndefined(c.baseSalary)
    if (baseSalary !== undefined) out.baseSalary = baseSalary
  } else {
    const baseSalary = toNumberOrUndefined(c.baseSalary)
    if (baseSalary !== undefined) out.baseSalary = baseSalary
    const hourlyRate = toNumberOrUndefined(c.hourlyRate)
    if (hourlyRate !== undefined) out.hourlyRate = hourlyRate
  }
  const currency = trim(c.currency)
  if (currency !== undefined) out.currency = currency
  const bankDetails = {
    bankName: trim(c.bankDetails.bankName),
    accountNumber: trim(c.bankDetails.accountNumber),
  }
  if (Object.values(bankDetails).some((v) => v !== undefined)) {
    out.bankDetails = Object.fromEntries(
      Object.entries(bankDetails).filter(([, v]) => v !== undefined)
    )
  }
  const taxInfo = {
    taxNumber: trim(c.taxInfo.taxNumber),
    taxBracket: trim(c.taxInfo.taxBracket),
    taxExempt: c.taxInfo.taxExempt,
  }
  if (Object.values(taxInfo).some((v) => v !== undefined)) {
    out.taxInfo = Object.fromEntries(
      Object.entries(taxInfo).filter(([, v]) => v !== undefined)
    )
  }
  return out
}

/** Hidden national ID / date of birth are left out; the API keeps the stored values. */
export function formValuesToUpdateUserPayload(
  values: EditUserFormValues,
  access: UserFieldAccess = FULL_ACCESS
): UpdateUserPayload {
  const payload: UpdateUserPayload = {
    email: values.email.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
  }
  const avatar = trim(values.avatar)
  if (avatar !== undefined) payload.avatar = avatar
  const personalInfo = compactPersonalInfo(values.personalInfo)
  if (personalInfo && !access.sensitive) {
    delete personalInfo.dateOfBirth
    delete personalInfo.cnicNumber
    delete personalInfo.passportNumber
  }
  if (personalInfo && Object.keys(personalInfo).length > 0) payload.personalInfo = personalInfo
  const contactInfo = compactContactInfo(values.contactInfo)
  if (contactInfo) payload.contactInfo = contactInfo
  return payload
}

export function formValuesToEmploymentPayload(
  values: EditUserFormValues
): UpdateEmploymentPayload | undefined {
  const employment = values.employment
  const out: UpdateEmploymentPayload = {}
  const department = trim(employment.department)
  if (department !== undefined) out.department = department
  const designation = trim(employment.designation)
  if (designation !== undefined) out.designation = designation
  if (employment.employmentType) out.employmentType = employment.employmentType
  const joiningDate = normalizeDate(employment.joiningDate)
  if (joiningDate !== undefined) out.joiningDate = joiningDate
  if (employment.workLocation) out.workLocation = employment.workLocation
  return Object.keys(out).length > 0 ? out : undefined
}

export function userToEditFormValues(user: User): EditUserFormValues {
  const pi = (user.personalInfo ?? {}) as Record<string, unknown>
  const ci = (user.contactInfo ?? {}) as Record<string, unknown>
  const employment = (user.employment ?? {}) as Record<string, unknown>
  const comp = (user.compensation ?? {}) as Record<string, unknown>
  const bankDetails = (comp.bankDetails ?? {}) as Record<string, unknown>
  const taxInfo = (comp.taxInfo ?? {}) as Record<string, unknown>
  const ec = (pi.emergencyContact ?? {}) as Record<string, unknown>
  const addr = (ci.address ?? {}) as Record<string, unknown>
  const str = (v: unknown): string => (typeof v === 'string' ? v : '')
  const numStr = (v: unknown): string =>
    typeof v === 'number' && Number.isFinite(v) ? String(v) : ''
  const dateStr = (v: unknown): string => {
    const raw = str(v).trim()
    if (!raw) return ''
    return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
  }
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    avatar: user.avatar ?? '',
    personalInfo: {
      dateOfBirth: dateStr(pi.dateOfBirth),
      gender: str(pi.gender) as EditUserFormValues['personalInfo']['gender'],
      nationality: str(pi.nationality),
      cnicNumber: str(pi.cnicNumber),
      passportNumber: str(pi.passportNumber),
      maritalStatus: str(pi.maritalStatus) as EditUserFormValues['personalInfo']['maritalStatus'],
      emergencyContact: {
        name: str(ec.name),
        relationship: str(ec.relationship),
        phone: str(ec.phone),
        email: str(ec.email),
      },
    },
    contactInfo: {
      personalEmail: str(ci.personalEmail),
      phone: str(ci.phone),
      alternatePhone: str(ci.alternatePhone),
      address: {
        street: str(addr.street),
        city: str(addr.city),
        state: str(addr.state),
        country: str(addr.country),
        postalCode: str(addr.postalCode),
      },
    },
    employment: {
      department: str(employment.department),
      designation: str(employment.designation),
      employmentType: str(employment.employmentType) as
        EditUserFormValues['employment']['employmentType'],
      joiningDate: dateStr(employment.joiningDate),
      workLocation: str(employment.workLocation) as
        EditUserFormValues['employment']['workLocation'],
    },
    compensation: {
      compensationType: str(comp.compensationType) as
        EditUserFormValues['compensation']['compensationType'],
      baseSalary: numStr(comp.baseSalary),
      hourlyRate: numStr(comp.hourlyRate),
      currency: str(comp.currency),
      bankDetails: {
        bankName: str(bankDetails.bankName),
        accountNumber: str(bankDetails.accountNumber),
      },
      taxInfo: {
        taxNumber: str(taxInfo.taxNumber),
        taxBracket: str(taxInfo.taxBracket),
        taxExempt: typeof taxInfo.taxExempt === 'boolean' ? taxInfo.taxExempt : false,
      },
    },
  }
}
